'use client'

import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ButtonLG } from '../ui/button-lg'
import type { SearchIndexItem } from '../../lib/docs-nav'

interface Match extends SearchIndexItem {
  score: number
}

function getMatches(pathname: string, searchIndex: SearchIndexItem[]): Match[] {
  const tokens = pathname
    .toLowerCase()
    .split(/[\/\-_\s]+/)
    .filter((t) => t.length > 2)

  if (tokens.length === 0) return []

  return searchIndex
    .map((item) => {
      const haystack =
        `${item.title} ${item.path} ${item.section} ${item.content ?? ''}`.toLowerCase()
      const score = tokens.reduce(
        (acc, token) => acc + (haystack.includes(token) ? 1 : 0),
        0,
      )
      return { ...item, score }
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
}

export function NotFoundClient({
  searchIndex,
}: {
  searchIndex: SearchIndexItem[]
}) {
  const [matches, setMatches] = useState<Match[]>([])
  const [pathname, setPathname] = useState('')
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const p = window.location.pathname
    setPathname(p)
    setMatches(getMatches(p, searchIndex))
    setReady(true)
  }, [searchIndex])

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="text-center"
    >
      <p className="font-mono text-sm font-semibold tracking-widest text-teal-500 uppercase">
        404
      </p>
      <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl dark:text-white">
        Page not found
      </h1>
      <p className="mt-4 text-lg text-slate-500 dark:text-slate-400">
        {pathname ? (
          <>
            <span className="font-mono text-sm text-slate-400 dark:text-slate-500">
              {pathname}
            </span>{' '}
            <br className="sm:hidden" />
            doesn&apos;t exist.
          </>
        ) : (
          "Sorry, we couldn't find the page you're looking for."
        )}
      </p>

      <AnimatePresence>
        {ready && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="mt-10"
          >
            {matches.length > 0 ? (
              <>
                <p className="mb-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  Did you mean one of these?
                </p>
                <ul className="mx-auto mb-8 max-w-md space-y-2 text-left">
                  {matches.map((match) => (
                    <li key={match.path}>
                      <a
                        href={match.path}
                        className="group flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 transition-all hover:border-teal-400 hover:shadow-sm dark:border-slate-700 dark:bg-slate-800/50 dark:hover:border-teal-500"
                      >
                        <span className="mt-0.5 text-teal-500">→</span>
                        <span className="flex flex-col">
                          <span className="font-medium text-slate-900 group-hover:text-teal-600 dark:text-white dark:group-hover:text-teal-400">
                            {match.title}
                          </span>
                          <span className="font-mono text-xs text-slate-400 dark:text-slate-500">
                            {match.path}
                          </span>
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="mb-8 text-sm text-slate-400 dark:text-slate-500">
                Couldn&apos;t find any related topic.
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-wrap justify-center gap-3">
        <ButtonLG href="/" variant="primary" ariaLabel="Go to home page">
          Go Home
        </ButtonLG>
        <ButtonLG
          href="/docs/"
          variant="secondary"
          ariaLabel="Browse documentation"
        >
          Browse Docs
        </ButtonLG>
      </div>
    </motion.div>
  )
}
